import { ChangeDetectorRef, Component, OnInit, OnDestroy, inject } from "@angular/core";
import { CommonModule } from "@angular/common";
import { LibraryModule } from "../../app/shared/lib/lib.module";
import { RouterModule, Router } from "@angular/router";
import { trigger, transition, style, animate, query, stagger, state } from "@angular/animations";
import { Subscription } from "rxjs";
import { ProgramService } from "../core/services/program.service";
import { LanguageService } from "../core/services/language.service";
import { getImageUrl as buildImageUrl } from "../shared/utils/commenFunctions";
import { PermissionService } from "../core/services/permission.service";
import { StaffPermission } from "../core/constants/staff-roles";

type ProgramCard = {
  id: string;
  titleKey: string;
  descriptionKey: string;
  ariaKey: string;
  route: string[];
  queryParams?: Record<string, string>;
  image: string;
  width: number;
  height: number;
  loading: 'eager' | 'lazy';
  fetchPriority: 'high' | 'low' | 'auto';
  decoding: 'async' | 'sync' | 'auto';
  cardClass: string;
  imgClass: string;
};

@Component({
  selector: "senior-center-tools-component",
  standalone: true,
  templateUrl: './senior-center-tools.component.html',
  styleUrls: ['./programs.component.css'],
  imports: [CommonModule, LibraryModule, RouterModule],
  animations: [
    trigger('pageTransition', [
      state('hidden', style({
        opacity: 0,
        visibility: 'hidden'
      })),
      state('visible', style({
        opacity: 1,
        visibility: 'visible'
      })),
      transition('hidden => visible', [
        animate('400ms ease-out')
      ])
    ]),
    trigger('cardLoad', [
      transition(':enter', [
        query('a', [
          style({ opacity: 0, transform: 'scale(0.9)' }),
          stagger(100, [
            animate('400ms ease-out', style({ opacity: 1, transform: 'scale(1)' }))
          ])
        ], { optional: true })
      ])
    ]),
    trigger('cardClick', [
      transition('* => clicked', [
        animate('300ms ease-in-out', style({ opacity: 0, transform: 'scale(0.95)' }))
      ])
    ])
  ]
})
export class SeniorCenterToolsComponent implements OnInit, OnDestroy {
  private readonly languageService = inject(LanguageService);
  private readonly cdr = inject(ChangeDetectorRef);
  private languageSub?: Subscription;

  clickedCard: string | null = null;
  pageTransitionState: 'hidden' | 'visible' = 'visible';
  staffInfoData: any = null;
  isLoading: boolean = true;
  imagesLoaded: Record<string, boolean> = {};
  private cachedProgramImages: Record<string, string> = {};
  private isRedirecting: boolean = false;
  private readonly seniorCenterProgramId = 1000;
  private readonly cardShellClass =
    'flex flex-col shrink-0 rounded-[8px] justify-start items-start bg-[#ffffffff] border-[2px] w-[314px] no-underline cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#0f172aff] hover:shadow-[0px_4px_12px_0px_rgba(0,0,0,0.15)] hover:-translate-y-1 hover:scale-105 transition-all duration-300 ease-out';
  private readonly seniorCenterApplicationsCard: ProgramCard = {
    id: 'senior-center-applications',
    titleKey: 'seniorTools.card.applications.title',
    descriptionKey: 'seniorTools.card.applications.description',
    ariaKey: 'seniorTools.card.applications.aria',
    route: ['/senior-center-applications'],
    image: 'assets/images/utility-assistance-program-2.png',
    width: 314,
    height: 314,
    loading: 'eager',
    fetchPriority: 'high',
    decoding: 'async',
    cardClass: this.cardShellClass,
    imgClass: 'w-full h-[314px] shrink-0 justify-start items-start object-cover rounded-t-[8px]',
  };
  private readonly staffManagementCard: ProgramCard = {
    id: 'staff-management',
    titleKey: 'seniorTools.card.staff.title',
    descriptionKey: 'seniorTools.card.staff.description',
    ariaKey: 'seniorTools.card.staff.aria',
    route: ['/staff-management'],
    image: 'assets/images/staff-management.png',
    width: 314,
    height: 314,
    loading: 'eager',
    fetchPriority: 'high',
    decoding: 'async',
    cardClass: this.cardShellClass,
    imgClass: 'w-full h-[314px] object-contain justify-start items-start rounded-t-[8px]',
  };
  private readonly seniorCenterRegistrationCard: ProgramCard = {
    id: 'staff-enrollment',
    titleKey: 'seniorTools.card.registration.title',
    descriptionKey: 'seniorTools.card.registration.description',
    ariaKey: 'seniorTools.card.registration.aria',
    route: ['/program/1000'],
    queryParams: { staffEnrollment: 'true' },
    image: '',
    width: 314,
    height: 314,
    loading: 'lazy',
    fetchPriority: 'low',
    decoding: 'async',
    cardClass: this.cardShellClass,
    imgClass: 'w-full h-[314px] shrink-0 justify-start items-start object-cover rounded-t-[8px]',
  };
  readonly cards: ProgramCard[] = [
    {
      id: 'card-checkin',
      titleKey: 'seniorTools.card.checkin.title',
      descriptionKey: 'seniorTools.card.checkin.description',
      ariaKey: 'seniorTools.card.checkin.aria',
      route: ['/card-checkin'],
      image: 'a347370febad46f99625eb53a4c4cef1.webp',
      width: 314,
      height: 314,
      loading: 'eager',
      fetchPriority: 'high',
      decoding: 'async',
      cardClass: this.cardShellClass,
      imgClass: 'w-full h-[314px] object-contain justify-start items-start rounded-t-[7px]',
    },
    {
      id: 'staff-delivery-layout-check-in',
      titleKey: 'seniorTools.card.delivery.title',
      descriptionKey: 'seniorTools.card.delivery.description',
      ariaKey: 'seniorTools.card.delivery.aria',
      route: ['/staff-delivery-layout'],
      image: '23f0bbcebe0848ba8e4f31ae3f5537aa.webp',
      width: 314,
      height: 314,
      loading: 'eager',
      fetchPriority: 'high',
      decoding: 'async',
      cardClass: this.cardShellClass,
      imgClass: 'w-full h-[314px] shrink-0 justify-start items-start object-cover rounded-t-[8px]',
    },
  ];

  constructor(
    private readonly router: Router,
    private readonly programService: ProgramService,
    private readonly permissionService: PermissionService,
  ) {}

  get loadingLabel(): string {
    return this.languageService.t('programs.loading');
  }

  get welcomeBefore(): string {
    return this.languageService.t('programs.welcomeNamed.before');
  }

  get welcomeAfter(): string {
    return this.languageService.t('programs.welcomeNamed.after');
  }

  get subtitle(): string {
    return this.languageService.t('seniorTools.subtitle');
  }

  trackByCardId(_index: number, card: ProgramCard): string {
    return card.id;
  }

  cardTitle(card: ProgramCard): string {
    return this.languageService.t(card.titleKey);
  }

  cardDescription(card: ProgramCard): string {
    return this.languageService.t(card.descriptionKey);
  }

  cardAriaLabel(card: ProgramCard): string {
    return this.languageService.t(card.ariaKey);
  }

  ngOnInit(): void {
    this.isLoading = true;
    this.languageSub = this.languageService.language$.subscribe(() => this.cdr.markForCheck());
    this.loadCachedProgramImages();

    const staffInfo = sessionStorage.getItem('staffInfo');
    if (staffInfo) {
      this.staffInfoData = JSON.parse(staffInfo);
    }

    if (this.permissionService.hasPermission(StaffPermission.CreateApplicationOnBehalf)) {
      this.cards.push(this.seniorCenterRegistrationCard);
    }

    if (this.permissionService.hasPermission(StaffPermission.ReviewApplications)) {
      this.cards.push(this.seniorCenterApplicationsCard);
    }

    if (this.permissionService.hasPermission(StaffPermission.ManageStaff)) {
      this.cards.push(this.staffManagementCard);
    }

    this.cards.forEach(card => {
      this.imagesLoaded[card.id] = false;
    });

    const token = sessionStorage.getItem('token');
    const staffInfoCheck = sessionStorage.getItem('staffInfo');
    if (token && staffInfoCheck) {
      this.loadSeniorCenterCardImage();
      this.getPrograms();
    } else {
      this.isLoading = true;
      this.redirectToSignIn('Please sign in to continue.');
    }
  }

  private loadCachedProgramImages(): void {
    if (typeof window === 'undefined') {
      return;
    }

    const cachedImages = sessionStorage.getItem('programImageCache');
    if (!cachedImages) {
      return;
    }

    try {
      this.cachedProgramImages = JSON.parse(cachedImages) ?? {};
    } catch {
      this.cachedProgramImages = {};
    }
  }

  onCardClick(route: string[], cardId: string, event: Event, queryParams?: Record<string, string>): void {
    event.preventDefault();
    this.clickedCard = cardId;

    if (cardId === 'staff-enrollment') {
      try {
        sessionStorage.setItem('programName', this.languageService.t('seniorTools.programName'));
        sessionStorage.removeItem('staff_enrollment_user_id');
        sessionStorage.removeItem('staff_enrollment_session_user_id');
        sessionStorage.removeItem('participant_auth_token');
        localStorage.removeItem('user_id');
      } catch {
        /* ignore */
      }
    }

    setTimeout(() => {
      this.router.navigate(route, queryParams ? { queryParams } : undefined);
    }, 300);
  }

  private redirectToSignIn(errorMessage?: string): void {
    if (this.isRedirecting) {
      return;
    }

    this.isRedirecting = true;
    this.isLoading = true;

    if (errorMessage) {
      const errorParam = encodeURIComponent(errorMessage);
      setTimeout(() => {
        this.router.navigate(['/'], {
          replaceUrl: true,
          queryParams: { error: errorParam }
        });
      }, 0);
    } else {
      setTimeout(() => {
        this.router.navigate(['/'], { replaceUrl: true });
      }, 0);
    }
  }

  ngOnDestroy(): void {
    this.languageSub?.unsubscribe();
  }

  private loadSeniorCenterCardImage(): void {
    this.programService.getProgramById(this.seniorCenterProgramId).subscribe({
      next: (response) => {
        const coverImageUrl = this.extractSeniorCenterCoverImageUrl(response);
        if (!coverImageUrl) {
          return;
        }

        this.applySeniorCenterCoverImage('staff-enrollment', coverImageUrl);
      },
    });
  }

  private extractSeniorCenterCoverImageUrl(response: unknown): string {
    const root = response as Record<string, unknown> | null | undefined;
    const program = (root?.['program'] ?? root?.['Program']) as Record<string, unknown> | undefined;
    const coverImageUrl = program?.['coverImageUrl'] ?? program?.['CoverImageUrl'];
    return typeof coverImageUrl === 'string' ? coverImageUrl.trim() : '';
  }

  private applySeniorCenterCoverImage(cardId: string, coverImageUrl: string): void {
    const card = this.cards.find((item) => item.id === cardId);
    if (!card) {
      return;
    }

    card.image = coverImageUrl;
    this.imagesLoaded[card.id] = false;
    this.cdr.markForCheck();
  }

  getPrograms(): void {
    const payload = {
      is_checkin: true,
    };
    this.programService.getPrograms(payload).subscribe({
      next: (response) => {
        if (response.programList?.length > 0) {
          this.cards.forEach((card) => {
            if (card.id === 'card-checkin') {
              const programId = String(response.programList[0].id);
              sessionStorage.setItem('programId', programId);
              card.route = ['/card-checkin', programId];
            }
          });
        }
        setTimeout(() => {
          this.isLoading = false;
          this.cdr.markForCheck();
        }, 300);
      },
      error: () => {
        setTimeout(() => {
          this.isLoading = false;
          this.cdr.markForCheck();
        }, 300);
      }
    });
  }

  getImageUrl(image: string): string {
    if (!image) {
      return '';
    }

    if (this.isLocalAsset(image) || this.isAbsoluteUrl(image)) {
      return this.normalizeAssetPath(image);
    }

    const cached = this.cachedProgramImages[image];
    if (cached) {
      return cached;
    }

    const remoteUrl = buildImageUrl(image);
    this.cachedProgramImages[image] = remoteUrl;
    this.persistCachedImages();
    return remoteUrl;
  }

  private isLocalAsset(path: string): boolean {
    return path.startsWith('assets/') || path.startsWith('/assets/');
  }

  private isAbsoluteUrl(path: string): boolean {
    return /^https?:\/\//i.test(path) || path.startsWith('data:');
  }

  private normalizeAssetPath(path: string): string {
    return path.startsWith('/') ? path : `/${path}`;
  }

  private persistCachedImages(): void {
    if (typeof window === 'undefined') {
      return;
    }
    sessionStorage.setItem('programImageCache', JSON.stringify(this.cachedProgramImages));
  }

  onImageLoad(cardId: string): void {
    this.imagesLoaded[cardId] = true;
  }

  onImageError(cardId: string): void {
    this.imagesLoaded[cardId] = true;
  }
}
